/**
 * Read-only special helpers (owner: specials team). Pure and allocation-free; shared by `specials/update.ts`, the
 * CPU opponent (`@hf/ai`) and anything else that must know whether pressing Special does something right now
 * without peeking at hidden information.
 *
 * ── When a special can be pressed (ARCHITECTURE §5 Controls "Specials — when they can be pressed") ──────────
 *  - common: the player has a special (`id` ≠ NONE: specials Off / STANDARD-reserved give NONE), it is still
 *    IDLE this play (one special per play) and power is full (`power ≥ POWER.max`).
 *  - HAIL_MARY: QB mode (`isQbMode`), RUSH_COUNT / LIVE.
 *  - BULLDOZER, MIRAGE: the OFFENSE player holds the ball (QB mode or carrier), RUSH_COUNT / LIVE.
 *  - BLITZ: the DEFENSE player in PRE_SNAP (armed secretly; revealed at the snap).
 *  - Classic kicks: never a Hail Mary on a kick play; nothing on a kickoff / onside toss (Bulldozer / Mirage stay
 *    open to a punter who runs: the fake punt); no Blitz against a kickoff.
 *  - Never in DEAD / CONVERSION / GAME_OVER. Pose-based activations (everything but Blitz) also wait until the
 *    player is free to pose (`canPose`): a press made while e.g. tackled, stunned or mid-throw stays buffered for
 *    MOVE.inputBufferFrames and fires as soon as the player is free.
 */
import { POWER, SPECIALS_TUNING, SpecialId } from '@hf/shared';
import { BallState, Phase, PlayCall, PlayerState, SpecialPhase, isKickCall } from '../enums';
import { isQbMode } from '../selectors';
import type { PlayerSim, SimState } from '../state';

/**
 * States a player may leave for SPECIAL_POSE (the 18 f telegraph pose). Committed moves (lunges, spins, truck
 * bursts, the throw release, fake-jump freeze/slam), locks (stun, stumble, tackled) and poses are excluded.
 */
export const canPose = (st: PlayerState): boolean =>
  st === PlayerState.IDLE ||
  st === PlayerState.RUN ||
  st === PlayerState.DASH ||
  st === PlayerState.BACKSTEP ||
  st === PlayerState.AIRBORNE ||
  st === PlayerState.LANDING ||
  st === PlayerState.TRUCK_CHARGE ||
  st === PlayerState.CHARGING_THROW ||
  st === PlayerState.PUMP_FAKE ||
  st === PlayerState.CATCH;

const livePhase = (s: SimState): boolean =>
  s.rules.phase === Phase.RUSH_COUNT || s.rules.phase === Phase.LIVE;

/** The offense player holds the ball (QB mode or carrier) in a live play. */
const offenseHolds = (s: SimState, i: number): boolean =>
  livePhase(s) && i === s.rules.offense && s.ball.state === BallState.HELD && s.ball.holder === i;

/**
 * Would a Special press by player `i` activate (or, for Blitz, arm) the special on this tick? Ignores the
 * press buffer. Safe for the AI: it reads only the asking player's own special and public state.
 */
export const specialAvailable = (s: SimState, i: number): boolean => {
  const p: PlayerSim = s.players[i === 0 ? 0 : 1];
  const sp = p.special;
  if (sp.id === SpecialId.NONE || sp.phase !== SpecialPhase.IDLE || p.power < POWER.max) return false;
  // Cooldown (handegg-roster.md): started on SPECIAL_END, ticked in specials/update.ts. Blitz has none — it is
  // capped at `usesPerDrive` arms per drive instead (reset in state.ts `startDrive`).
  if (sp.id === SpecialId.BLITZ) {
    if (sp.usesThisDrive >= SPECIALS_TUNING.blitz.usesPerDrive) return false;
  } else if (sp.cooldownFrames > 0) return false;
  // Classic kicks: no Hail Mary toss, nothing for the kickoff kicker (a punter who runs may still Bulldoze /
  // Mirage — the fake punt), no Blitz against a kickoff.
  const call = s.rules.playCall;
  switch (sp.id) {
    case SpecialId.HAIL_MARY:
      return !isKickCall(call) && isQbMode(s, i) && canPose(p.state);
    case SpecialId.BULLDOZER:
    case SpecialId.MIRAGE:
    case SpecialId.ROCKET_BOOTS:
      return call !== PlayCall.KICKOFF && call !== PlayCall.ONSIDE && offenseHolds(s, i) && canPose(p.state);
    case SpecialId.BLITZ:
      return s.rules.phase === Phase.PRE_SNAP && i !== s.rules.offense && call !== PlayCall.KICKOFF;
    case SpecialId.BIG_HEAD:
    case SpecialId.GROUND_POUND:
      return livePhase(s) && i !== s.rules.offense && canPose(p.state);
    case SpecialId.DOUBLE_JUMP:
    case SpecialId.MAGNET_ONSIDE:
      return livePhase(s) && canPose(p.state);
    default:
      // STANDARD pool specials are reserved (ARCHITECTURE §5 Characters): nothing happens.
      return false;
  }
};

/** Big Head (defense) is inflated right now: bigger grab/swat/loose-recovery reach, a flat speed penalty. */
export const bigHeadActive = (p: PlayerSim): boolean =>
  p.special.id === SpecialId.BIG_HEAD && p.special.phase === SpecialPhase.ACTIVE;

/** Rocket Boots (offense) is burning right now: a flat speed bonus. */
export const rocketBootsActive = (p: PlayerSim): boolean =>
  p.special.id === SpecialId.ROCKET_BOOTS && p.special.phase === SpecialPhase.ACTIVE;

/** `p`'s head radius for hitbox purposes this tick (Big Head's `radiusMul` while ACTIVE, else the plain stat). */
export const effectiveHeadRadius = (p: PlayerSim): number =>
  p.stats.headRadius * (bigHeadActive(p) ? SPECIALS_TUNING.bigHead.radiusMul : 1);

/**
 * The special phase of player `i` as the OTHER player (or a spectator / HUD / AI) may know it: a secretly ARMED
 * Blitz reads as IDLE (hidden information, ARCHITECTURE §5 "Blitz timing"). Only the owner's own UI may show ARMED.
 */
export const publicSpecialPhase = (s: SimState, i: number): SpecialPhase => {
  const ph = s.players[i === 0 ? 0 : 1].special.phase;
  return ph === SpecialPhase.ARMED ? SpecialPhase.IDLE : ph;
};

/** A Mirage decoy is up for player `i` (the CPU must treat both heads as candidates while it is). */
export const decoyUp = (s: SimState, i: number): boolean => s.players[i === 0 ? 0 : 1].special.decoyActive;
